// Localization Popup JavaScript with Modal Flow Control
class LocalizationPopup {
  constructor() {
    this.popup = document.getElementById('country-language-popup');
    this.form = document.getElementById('country-language-form');
    this.closeBtn = this.popup?.querySelector('.popup-close');
    this.confirmBtn = this.popup?.querySelector('.popup-confirm-btn');
    this.countrySelect = this.popup?.querySelector('#country-selector');
    this.languageSelect = this.popup?.querySelector('#language-selector');
    this.cookieBanner = document.getElementById('shopify-pc__banner');

    this.storageKey = 'localization-popup-dismissed';
    this.confirmationKey = 'localization-confirmed';
    this.delayMs = parseInt(window.popupSettings?.delay) || 1000;
    this.isEnabled = window.popupSettings?.enabled !== false;

    this.locale = document.documentElement.lang || navigator.language || 'en-US';

    this.init();
  }

  init() {
    if (!this.popup || !this.isEnabled) return;

    this.bindEvents();
    this.cleanCountryLabels();
    this.checkAndShowPopup();
  }

  bindEvents() {
    // Remove close button functionality - user must confirm
    // this.closeBtn?.addEventListener('click', () => this.hidePopup());

    // Prevent closing by clicking outside
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) {
        e.preventDefault();
        e.stopPropagation();
        // Do nothing - user must confirm
      }
    });

    // Prevent closing with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('show')) {
        e.preventDefault();
        e.stopPropagation();
        // Do nothing - user must confirm
      }
    }, true);

    this.form?.addEventListener('submit', (e) => this.handleFormSubmit(e));
    this.countrySelect?.addEventListener('change', () => this.updateLanguageOptions());
  }

  checkAndShowPopup() {
    const isConfirmed = localStorage.getItem(this.confirmationKey);

    // If user already confirmed, allow normal page interaction
    if (isConfirmed) {
      this.allowPageInteraction();
      return;
    }

    // Hide cookie banner initially
    this.hideCookieBanner();

    // Show popup after delay and block interaction
    setTimeout(() => {
      this.showPopup();
      this.blockPageInteraction();
    }, this.delayMs);
  }

  blockPageInteraction() {
    // Prevent scrolling
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    // Ensure popup is blocking
    if (this.popup) {
      this.popup.style.display = 'flex';
      this.popup.style.position = 'fixed';
      this.popup.style.top = '0';
      this.popup.style.left = '0';
      this.popup.style.width = '100%';
      this.popup.style.height = '100%';
      this.popup.style.zIndex = '999999';
      this.popup.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    }

    // Keep cookie banner hidden
    this.hideCookieBanner();
  }

  allowPageInteraction() {
    // Restore scrolling
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    // Hide localization popup
    if (this.popup) {
      this.popup.style.display = 'none';
      this.popup.classList.remove('show');
    }

    // Show cookie banner
    this.showCookieBanner();
  }

  hideCookieBanner() {
    if (this.cookieBanner) {
      this.cookieBanner.style.display = 'none';
    }
  }

  showCookieBanner() {
    if (this.cookieBanner) {
      this.cookieBanner.style.display = '';
    }
  }

  showPopup() {
    if (!this.popup) return;

    this.popup.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.popup.classList.add('show');
    });

    this.trackEvent('popup_viewed');
  }

  hidePopup() {
    if (!this.popup) return;

    this.popup.classList.remove('show');

    setTimeout(() => {
      this.allowPageInteraction();
    }, 300);

    // Mark as confirmed and dismissed
    localStorage.setItem(this.confirmationKey, 'true');
    localStorage.setItem(this.storageKey, 'true');
    this.trackEvent('popup_dismissed');
  }

  async handleFormSubmit(e) {
    e.preventDefault();
    if (!this.form) return;

    const formData = new FormData(this.form);
    const countryCode = formData.get('country_code');
    const languageCode = formData.get('language_code');

    this.setLoadingState(true);

    try {
      this.trackEvent('localization_selected', { country: countryCode, language: languageCode });

      const response = await fetch('/localization', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (response.ok) {
        // Mark as confirmed
        localStorage.setItem(this.confirmationKey, 'true');
        localStorage.setItem(this.storageKey, 'true');
        sessionStorage.setItem('localization-updated', 'true');

        this.hidePopup();

        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Localization update failed:', error);
      // Mark as confirmed even on error to avoid infinite loop
      localStorage.setItem(this.confirmationKey, 'true');
      this.form.submit(); // fallback
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(loading) {
    if (!this.confirmBtn) return;
    this.confirmBtn.classList.toggle('loading', !!loading);
    this.confirmBtn.disabled = !!loading;
  }

  updateLanguageOptions() {
    console.log('Country changed to:', this.countrySelect?.value);
  }

  trackEvent(eventName, data = {}) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, { event_category: 'localization_popup', ...data });
    }
    if (typeof fbq === 'function') {
      fbq('trackCustom', eventName, data);
    }
    if (window.analytics?.track) {
      window.analytics.track(eventName, data);
    }
    if (window.ShopifyAnalytics?.lib?.track) {
      window.ShopifyAnalytics.lib.track(eventName, data);
    }
  }

  // Remove currency codes & symbols entirely from <option> labels
  cleanCountryLabels() {
    if (!this.countrySelect) return;
    if (this.countrySelect.dataset.cleaned === '1') return;

    const options = Array.from(this.countrySelect.options);

    options.forEach(opt => {
      let base = (opt.label || opt.textContent || '')
        .replace(/\s*(\([^()]*\)\s*)+$/g, '') // remove any (...) at END
        .replace(/\s+/g, ' ')
        .trim();

      opt.label = base;
      opt.textContent = base;
    });

    this.countrySelect.dataset.cleaned = '1';
  }

  // Public methods
  show() { 
    this.showPopup();
    this.blockPageInteraction();
  }
  
  hide() { this.hidePopup(); }
  
  reset() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.confirmationKey);
    sessionStorage.removeItem('geo-redirected');
    sessionStorage.removeItem('localization-updated');
  }
}

// Utility functions
const LocalizationUtils = {
  getUserLanguage() {
    return navigator.language || navigator.userLanguage || 'en';
  },

  async getUserCountry() {
    try {
      if (window.Shopify?.country) {
        return window.Shopify.country;
      }
      const response = await fetch('https://ipapi.co/country_code/', {
        method: 'GET',
        headers: { 'Accept': 'text/plain' }
      });
      if (response.ok) {
        return await response.text();
      }
    } catch (error) {
      console.warn('Could not determine user country:', error);
    }
    return null;
  },

  isOptimalSelection() {
    const userLang = this.getUserLanguage().split('-')[0];
    const currentLang = document.documentElement.lang || 'en';
    return userLang === currentLang;
  }
};

// Handle dropdown button clicks to open popup
function openLocalizationPopup(event) {
  event?.preventDefault();
  event?.stopPropagation();
  
  if (window.localizationPopup) {
    window.localizationPopup.show();
  }
}

// Make it globally accessible
window.openLocalizationPopup = openLocalizationPopup;

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.localizationPopup = new LocalizationPopup();
  window.LocalizationUtils = LocalizationUtils;

  // Attach click handler to localization dropdown buttons
  const localizationButtons = document.querySelectorAll('.dropdown-localization__button');
  
  localizationButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (window.localizationPopup) {
        window.localizationPopup.show();
      }
    });
  });
});

// Show in Theme Editor for preview
if (window.Shopify?.designMode) {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      window.localizationPopup?.show();
    }, 500);
  });
}